import { useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { Layout } from './components/layout';
import { 
  Login, 
  Dashboard, 
  Accounts,
  AccountDetail,
  AccountForm,
  Deals,
  DealDetail,
  DealForm,
  Activities,
  ActivityDetail,
  ActivityForm,
  Todos,
  Profile,
  ChangePassword,
  Invite,
  Reports,
  Settings,
} from './pages';
import { AdminUsers } from './pages/admin/Users';
import { API_BASE_URL, getAuthToken, clearAuthToken } from './lib/api';

interface User {
  id: string;
  name: string;
  email: string;
  role: string;
  avatar?: string;
}

// 認証済みルートのラッパー
function PrivateRoute({ children, user }: { children: React.ReactNode; user: User | null }) {
  const location = useLocation();

  if (!user) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  return <>{children}</>;
}

// ログイン済みユーザーをリダイレクト
function PublicRoute({ children, user }: { children: React.ReactNode; user: User | null }) {
  if (user) {
    return <Navigate to="/" replace />;
  }

  return <>{children}</>;
}

function AppRoutes({ user, setUser }: { user: User | null; setUser: (user: User | null) => void }) {
  const handleLogin = (loggedInUser: User) => {
    setUser(loggedInUser);
  };

  const handleUpdateUser = (updatedUser: User) => {
    setUser(updatedUser);
  };

  return (
    <Routes>
      {/* 公開ルート */}
      <Route 
        path="/login" 
        element={
          <PublicRoute user={user}>
            <Login onLogin={handleLogin} />
          </PublicRoute>
        } 
      />
      <Route 
        path="/invite/:token" 
        element={
          <PublicRoute user={user}>
            <Invite onLogin={handleLogin} />
          </PublicRoute>
        } 
      />

      {/* 認証が必要なルート */}
      <Route
        path="/"
        element={
          <PrivateRoute user={user}>
            <Layout user={user} />
          </PrivateRoute>
        }
      >
        <Route index element={<Dashboard user={user} />} />
        <Route path="accounts" element={<Accounts />} />
        <Route path="accounts/new" element={<AccountForm />} />
        <Route path="accounts/:id" element={<AccountDetail />} />
        <Route path="accounts/:id/edit" element={<AccountForm />} />
        <Route path="deals" element={<Deals />} />
        <Route path="deals/new" element={<DealForm />} />
        <Route path="deals/:id" element={<DealDetail />} />
        <Route path="deals/:id/edit" element={<DealForm />} />
        <Route path="activities" element={<Activities />} />
        <Route path="activities/new" element={<ActivityForm />} />
        <Route path="activities/:id" element={<ActivityDetail />} />
        <Route path="activities/:id/edit" element={<ActivityForm />} />
        <Route path="todos" element={<Todos />} />
        <Route path="reports" element={<Reports />} />
        <Route path="admin/users" element={<AdminUsers />} />
        <Route path="settings" element={<Settings />} />
        <Route 
          path="profile" 
          element={user ? <Profile user={user} onUpdate={handleUpdateUser} /> : null} 
        />
        <Route path="change-password" element={<ChangePassword />} />
      </Route>

      {/* 404 */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    // 現在のユーザー情報を取得
    const fetchCurrentUser = async () => {
      const token = getAuthToken();
      
      try {
        const headers: Record<string, string> = {};
        if (token) {
          headers['Authorization'] = `Bearer ${token}`;
        }

        const response = await fetch(`${API_BASE_URL}/api/auth/me`, {
          credentials: 'include',
          headers,
        });
        
        if (response.ok) {
          const data = await response.json();
          setUser(data.user);
        } else {
          // 認証失敗時はトークンをクリア
          clearAuthToken();
        }
      } catch (err) {
        console.error('Auth check failed:', err);
        clearAuthToken();
      } finally {
        setIsLoading(false);
      }
    };

    fetchCurrentUser();
  }, []);

  if (isLoading) {
    return (
      <div 
        className="min-h-screen flex items-center justify-center"
        style={{ backgroundColor: 'var(--background-main)' }}
      >
        <div className="text-center">
          <div className="w-16 h-16 rounded-2xl bg-blue-600 flex items-center justify-center mx-auto mb-4">
            <span className="text-white text-2xl font-bold">T's</span>
          </div>
          <p style={{ color: 'var(--text-secondary)' }}>読み込み中...</p>
        </div>
      </div>
    );
  }

  return (
    <BrowserRouter>
      <AppRoutes user={user} setUser={setUser} />
    </BrowserRouter>
  );
}

export default App;
